import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CREMONA_LOGO_DATA_URL } from './cremonaLogo';

// Nicely format a percent number: strip meaningless zeros, cap at 2 decimals.
// 13 -> "13", 13.5 -> "13.5", 13.04347826 -> "13.04", 20 -> "20"
const fmtPct = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '0';
  const rounded = Math.round(n * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/\.?0+$/, '');
};

const COMPANY_DEFAULT = {
  name: 'PT. Cremona Structure Engineering',
  address: 'Citra Indah City Blok F4 No.14, Jonggol, Bogor 16830',
  phone: '+62 812-3456-7890',
  email: 'info@cremona.id',
};

/**
 * Build an Invoice PDF document. Pure function — no React state.
 * Returns a jsPDF doc that you can .save() or use .output('blob').
 *
 * @param {Object} data
 * @param {string} data.invoiceNumber
 * @param {string} data.clientName
 * @param {string} data.clientCompany
 * @param {string} data.mode "milestone" | "project_list"
 * @param {Array}  data.milestoneItems
 * @param {string} data.milestoneProjectName (optional)
 * @param {Array}  data.projectBillingData
 * @param {number} data.totalAmount
 * @param {string} data.issueDate (YYYY-MM-DD)
 * @param {string} data.dueDate   (YYYY-MM-DD)
 * @param {Object} data.bankAccount { bank, account, holder }
 * @param {string} data.notes
 * @param {string} data.signatureFile (base64 data URL, optional)
 * @param {Object} data.signatureDims { w, h } (optional)
 * @param {Object} data.signaturePos  { x, y, w } (optional, mm)
 * @param {Object} [company]
 */
export const buildInvoicePDF = (data, company = COMPANY_DEFAULT) => {
  const {
    invoiceNumber, clientName, clientCompany, mode,
    milestoneItems = [], milestoneProjectName,
    projectBillingData = [], totalAmount,
    issueDate, dueDate, bankAccount = {}, notes,
    signatureFile, signatureDims, signaturePos,
    // Milestone mode only: pre-built note about previous payments on the linked project
    milestonePaidNote,
  } = data;

  const fmtRp = (v) => `Rp ${(v || 0).toLocaleString('id-ID')}`;
  const fmtDate = (d) => {
    if (!d) return '-';
    const [year, month, day] = d.split('-');
    return `${day}/${month}/${year}`;
  };

  const doc = new jsPDF();
  const pw = doc.internal.pageSize.getWidth();
  let y = 12;
  const BRAND = [27, 71, 117];
  const BRAND_LIGHT = [73, 117, 163];

  // === HEADER: Big centered logo ===
  const logoW = 90;
  const logoH = 19.4;
  const logoX = (pw - logoW) / 2;
  try {
    doc.addImage(CREMONA_LOGO_DATA_URL, 'PNG', logoX, y, logoW, logoH);
  } catch (e) { /* graceful */ }
  y += logoH + 4;

  // Address / contact info
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text(company.address, pw / 2, y, { align: 'center' }); y += 4;
  doc.text(`Tel: ${company.phone}  |  Email: ${company.email}`, pw / 2, y, { align: 'center' });
  y += 6;

  // Blue accent divider
  doc.setDrawColor(...BRAND); doc.setLineWidth(1.2);
  doc.line(14, y, pw - 14, y); y += 1.5;
  doc.setLineWidth(0.3); doc.setDrawColor(...BRAND_LIGHT);
  doc.line(14, y, pw - 14, y); y += 8;

  doc.setTextColor(0, 0, 0);

  // Invoice Title
  doc.setFontSize(20); doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND);
  doc.text('INVOICE', pw / 2, y, { align: 'center' }); y += 10;
  doc.setTextColor(0, 0, 0);

  // Invoice details
  doc.setFontSize(10); doc.setFont('helvetica', 'normal');
  doc.text('No. Invoice', 14, y); doc.text(`: ${invoiceNumber}`, 45, y);
  doc.text('Tanggal Terbit', pw - 95, y); doc.text(`: ${fmtDate(issueDate)}`, pw - 55, y);
  y += 6;

  if (mode === 'milestone' && milestoneProjectName) {
    doc.setFont('helvetica', 'bold');
    doc.text('Project', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`: ${milestoneProjectName}`, 45, y);
  }
  doc.setFont('helvetica', 'bold');
  doc.text('Tanggal Jatuh Tempo', pw - 95, y); doc.text(`: ${fmtDate(dueDate)}`, pw - 55, y);
  doc.setFont('helvetica', 'normal');
  y += 6;

  if (clientName) { doc.text('Kepada', 14, y); doc.text(`: ${clientName}`, 45, y); y += 5; }
  if (clientCompany) { doc.text(`  ${clientCompany}`, 45, y); y += 8; } else { y += 8; }

  // Table
  if (mode === 'milestone') {
    const head = [['No', 'Keterangan', 'Persen', 'Jumlah']];
    const body = milestoneItems.map((item, i) => [i + 1, item.description, `${fmtPct(item.percent || 0)}%`, fmtRp(item.amount)]);
    body.push([
      { content: 'TOTAL', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right', fillColor: [240, 246, 252], textColor: BRAND } },
      { content: fmtRp(totalAmount), styles: { fontStyle: 'bold', fillColor: [240, 246, 252], textColor: BRAND } },
    ]);
    autoTable(doc, {
      startY: y, head, body, theme: 'grid',
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: { fillColor: BRAND, textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 15 }, 2: { halign: 'center', cellWidth: 30 }, 3: { halign: 'right', cellWidth: 50 } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  } else {
    const head = [['No', 'Project', 'Nilai Kontrak', 'Sudah Dibayar (%)', 'Persen Tagihan', 'Jumlah Tagihan']];
    const body = projectBillingData.map((p, i) => [
      i + 1,
      p.projectName,
      fmtRp(p.contractValue),
      p.paidPercent != null ? `${Math.round(p.paidPercent)}%` : '—',
      `${fmtPct(p.billingPercent)}%`,
      fmtRp(p.amount),
    ]);
    body.push([
      { content: 'TOTAL', colSpan: 5, styles: { fontStyle: 'bold', halign: 'right', fillColor: [240, 246, 252], textColor: BRAND } },
      { content: fmtRp(totalAmount), styles: { fontStyle: 'bold', fillColor: [240, 246, 252], textColor: BRAND } },
    ]);
    autoTable(doc, {
      startY: y, head, body, theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: BRAND, textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 10 },
        2: { halign: 'right' },
        3: { halign: 'center', cellWidth: 26 },
        4: { halign: 'center', cellWidth: 22 },
        5: { halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Milestone paid note (only shown when there are actual past payments on the linked project)
  if (mode === 'milestone' && milestonePaidNote) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic');
    doc.setTextColor(...BRAND);
    doc.text(milestonePaidNote, 14, y, { maxWidth: pw - 28 });
    y += 6;
    doc.setTextColor(0, 0, 0);
  }

  // Notes
  if (notes) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic');
    doc.text(`Catatan: ${notes}`, 14, y); y += 8;
  }

  // Tax info
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('*Non-PKP (PPN tidak dipungut)', 14, y); y += 10;

  // Payment info — blue accent box
  const payBoxY = y;
  const payBoxH = 26;
  doc.setFillColor(240, 246, 252);
  doc.rect(14, payBoxY, pw - 28, payBoxH, 'F');
  doc.setFillColor(...BRAND);
  doc.rect(14, payBoxY, 2, payBoxH, 'F');
  doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND);
  doc.text('Pembayaran dapat ditransfer ke:', 20, y + 6); y += 11;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(`Bank: ${bankAccount.bank || ''}`, 20, y); y += 5;
  doc.text(`No. Rekening: ${bankAccount.account || ''}`, 20, y); y += 5;
  doc.text(`Atas Nama: ${bankAccount.holder || ''}`, 20, y);
  y = payBoxY + payBoxH + 12;

  // Signature block
  doc.setFont('helvetica', 'normal');
  const sigBlockX = pw - 80;
  doc.text('Hormat kami,', sigBlockX, y); y += 2;

  if (signatureFile && signatureDims) {
    const ratio = signatureDims.w / signatureDims.h;
    let sigX, sigY, sigW, sigH;
    if (signaturePos) {
      sigX = signaturePos.x; sigY = signaturePos.y; sigW = signaturePos.w; sigH = sigW / ratio;
    } else {
      const maxH = 50, maxW = 95;
      if (ratio >= maxW / maxH) { sigW = maxW; sigH = sigW / ratio; }
      else { sigH = maxH; sigW = sigH * ratio; }
      sigX = sigBlockX - 6; sigY = y;
    }
    try {
      const fmt = signatureFile.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(signatureFile, fmt, sigX, sigY, sigW, sigH);
    } catch (e) { /* graceful */ }
    y = signaturePos ? (sigY + sigH - 8) : (y + sigH - 8);
  } else {
    y += 25;
  }

  doc.setFont('helvetica', 'bold');
  doc.text('Johan Morrish Kabuaran', sigBlockX, y); y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('Principal Engineer', sigBlockX, y);

  return doc;
};

/**
 * Save an invoice PDF directly. Useful for the "Print PDF" button in
 * the postponed-invoices list.
 */
export const downloadInvoicePDF = (data, company) => {
  const doc = buildInvoicePDF(data, company);
  const safeClient = (data.clientName || 'Client').replace(/[/\\]/g, '-');
  const safeNum = (data.invoiceNumber || 'invoice').replace(/[/\\]/g, '-');
  doc.save(`Invoice_${safeNum}_${safeClient}.pdf`);
};
